import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";
import ActionButton from "@/components/base/ActionButton";

const reasons = [
  {
    icon: "ri-time-line",
    title: "Extend Vehicle Usability",
    description:
      "A vehicle that is structurally sound and still in use does not have to be discarded. Retrofit gives a suitable existing vehicle a longer working life.",
  },
  {
    icon: "ri-oil-line",
    title: "Reduce Fossil Fuel Dependence",
    description:
      "Electrifying a suitable vehicle reduces its dependence on petrol or diesel for day-to-day movement.",
  },
  {
    icon: "ri-flashlight-line",
    title: "Enable Electric Mobility",
    description:
      "Retrofit makes it possible to move into electric mobility using suitable vehicles that already exist on the road.",
  },
  {
    icon: "ri-wallet-3-line",
    title: "Potentially Lower Transition Cost",
    description:
      "Retrofit can potentially reduce the cost of transition compared with replacing the complete vehicle — subject to assessment.",
  },
  {
    icon: "ri-leaf-line",
    title: "Support Sustainable Transportation",
    description:
      "Extending vehicle life and shifting to electric drive supports more sustainable transportation outcomes.",
  },
  {
    icon: "ri-roadster-line",
    title: "Enable Fleet Electrification",
    description:
      "Fleets can begin electrification in phases instead of replacing entire vehicle fleets at once.",
  },
];

export default function WhyRetrofit() {
  return (
    <section className="w-full bg-background-50 py-16 md:py-24">
      <div className="mx-auto w-full max-w-container px-4 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
          <Reveal className="lg:col-span-5">
            <SectionHeading
              eyebrow="Why Retrofit"
              title="Why Replace the Vehicle When You Can Transform It?"
              description="Retrofitting keeps a suitable existing vehicle in service and converts it to electric drive. The question is not whether to move to electric mobility, but whether a vehicle can be transformed rather than replaced."
            />
            <div className="mt-8 overflow-hidden rounded-lg border border-background-200">
              <div className="h-56 w-full md:h-72">
                <img
                  src="https://readdy.ai/api/search-image?query=close%20up%20of%20an%20electric%20vehicle%20powertrain%20component%20being%20fitted%20into%20a%20vehicle%20bay%2C%20clean%20industrial%20workshop%2C%20graphite%20and%20green%20accent%20lighting%2C%20premium%20engineering%20photography%2C%20soft%20shadows&width=1200&height=900&seq=klz-why-01&orientation=landscape"
                  alt="Electric vehicle retrofit engineering work in a clean workshop"
                  title="EV retrofit engineering workshop"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <ActionButton to="/about" variant="dark" size="md" iconRight="ri-arrow-right-line">
                Our Approach
              </ActionButton>
              <ActionButton to="/ev-retrofit" variant="outline" size="md">
                What Is EV Retrofit?
              </ActionButton>
            </div>
          </Reveal>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5">
              {reasons.map((item, index) => (
                <Reveal key={item.title} delay={index * 0.06}>
                  <article className="h-full rounded-lg border border-background-200 bg-background-100 p-5 transition-colors duration-300 hover:border-primary-300 md:p-6">
                    <span className="flex h-11 w-11 items-center justify-center rounded-md bg-background-50 text-primary-700">
                      <i className={`${item.icon} text-xl`} />
                    </span>
                    <h3 className="mt-4 text-base font-semibold text-foreground-950">{item.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                      {item.description}
                    </p>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}